import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';
import type { AddUserToGroupsRequest } from 'pkg/actions/memberships/interface';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import * as sdk from 'pkg/core/sdk';
import type { OrganizationsLocalStorageData } from 'pkg/identity/account';
import * as localStorageHelpers from 'pkg/local_storage';

export const storeActiveMembership = (
	organizationId: number,
	membership: models.membership.Membership
) => {
	let m = { ...membership };
	m.user = null;
	m.group = null;
	const parsedLocalStorageOrgData =
		localStorageHelpers.getJsonItem<OrganizationsLocalStorageData>(
			localStorageHelpers.LocalStorageKeys.Organizations
		);

	if (parsedLocalStorageOrgData) {
		parsedLocalStorageOrgData[organizationId] = {
			...parsedLocalStorageOrgData[organizationId],
			activeMembership: m,
		};

		localStorageHelpers.setJsonItem(
			localStorageHelpers.LocalStorageKeys.Organizations,
			parsedLocalStorageOrgData
		);
	}
};

export const clearActiveMembership = (organizationId: number) => {
	const parsedLocalStorageOrgData =
		localStorageHelpers.getJsonItem<OrganizationsLocalStorageData>(
			localStorageHelpers.LocalStorageKeys.Organizations
		);

	if (parsedLocalStorageOrgData) {
		parsedLocalStorageOrgData[organizationId] = {
			...parsedLocalStorageOrgData[organizationId],
			activeMembership: null,
		};

		localStorageHelpers.setJsonItem(
			localStorageHelpers.LocalStorageKeys.Organizations,
			parsedLocalStorageOrgData
		);
	}
};

export const addUserToGroups = async (
	organizationId: number,
	userId: number,
	groups: AddUserToGroupsRequest[]
): Promise<boolean> => {
	const request = await sdk.post(
		endpoints.UserGroups.AddUserToGroups(organizationId, userId),
		{},
		groups
	);

	if (!request.ok) {
		flashActions.show(
			{
				title: t('Something went wrong'),
				message: t(
					"If you think this error isn't supposed to happen, please contact 360Player support."
				),
			},
			request.status
		);

		return false;
	}

	flashActions.show(
		{
			title: t(`User added`),
			message: t(`User was added successfully`),
		},
		request.status
	);

	return true;
};

interface RemoveUserFromGroupPayload {
	removeUserRecursively: boolean;
	removeFromFutureEvents: boolean;
}

export async function removeUserFromGroup(
	organizationId: number,
	groupId: number,
	userId: number,
	payload: RemoveUserFromGroupPayload
): Promise<boolean> {
	const req = await sdk.destroy(
		endpoints.UserGroups.RemoveUser(organizationId, groupId, userId),
		{},
		payload
	);

	if (!req.ok) {
		flashActions.show({
			title: t('Something went wrong'),
			message: t('Could not remove user'),
		});

		return false;
	}

	flashActions.show({
		title: t('Successfully removed user'),
	});

	return true;
}

interface RemoveUsersFromGroupPayload {
	userIds: number[];
	removeUsersRecursively?: boolean;
	removeFromFutureEvents?: boolean;
}

export async function removeUsersFromGroup(
	organizationId: number,
	groupId: number,
	payload: RemoveUsersFromGroupPayload
): Promise<boolean> {
	const req = await sdk.destroy(
		endpoints.UserGroups.RemoveUsers(organizationId, groupId),
		{},
		payload
	);

	if (!req.ok) {
		flashActions.show({
			title: t('Something went wrong'),
			message: t('Could not remove users'),
		});

		return false;
	}

	flashActions.show({
		title: t('Successfully removed users'),
	});

	return true;
}
